import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Search, Filter, Download, Eye, ChevronDown, ChevronUp,
  Map, Calendar, Users, FileText, ExternalLink
} from 'lucide-react';
import { treatyAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

// Helper function to format treaty dates correctly
const formatTreatyDate = (dateValue, options = { year: 'numeric', month: 'long', day: 'numeric' }) => {
  if (!dateValue) return 'Date unknown';
  
  try {
    // Handle ISO string format (e.g., "1846-05-15T00:00:00")
    if (typeof dateValue === 'string') {
      // Parse the date parts directly to avoid timezone issues
      const match = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        const [, year, month, day] = match;
        
        // Check if this is a January 1st placeholder (only year was known)
        if (month === '01' && day === '01') {
          // Just return the year since exact date is unknown
          return year;
        }
        
        // Create date using UTC to avoid timezone shifts
        const date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day)));
        return date.toLocaleDateString('en-US', { ...options, timeZone: 'UTC' });
      }
    }
    
    // Fallback: try standard Date parsing
    const date = new Date(dateValue);
    if (!isNaN(date.getTime())) {
      // Check for January 1st placeholder
      if (date.getMonth() === 0 && date.getDate() === 1) {
        return date.getFullYear().toString();
      }
      return date.toLocaleDateString('en-US', options);
    }
    
    return 'Date unknown';
  } catch (e) {
    console.error('Error parsing treaty date:', dateValue, e);
    return 'Date unknown';
  }
};

const TreatyLibrary = () => {
  const { toast } = useToast();
  const [treaties, setTreaties] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTribe, setSelectedTribe] = useState('all');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');
  const [expandedTreaty, setExpandedTreaty] = useState(null);
  const [selectedTreaty, setSelectedTreaty] = useState(null);
  const [showTreatyModal, setShowTreatyModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load treaties on component mount and when search parameters change
  useEffect(() => {
    loadTreaties();
  }, []);

  useEffect(() => {
    if (searchTerm || (selectedTribe && selectedTribe !== 'all') || (selectedRegion && selectedRegion !== 'all') || (selectedYear && selectedYear !== 'all')) {
      searchTreaties();
    } else {
      loadTreaties();
    }
  }, [searchTerm, selectedTribe, selectedRegion, selectedYear]);

  const loadTreaties = async () => {
    try {
      setIsLoading(true);
      const treatiesData = await treatyAPI.searchTreaties({});
      setTreaties(treatiesData);
    } catch (error) {
      console.error('Failed to load treaties:', error);
      toast({
        variant: "destructive",
        title: "Loading Error",
        description: "Failed to load treaty library. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const searchTreaties = async () => {
    try {
      setIsLoading(true);
      const searchParams = {};
      
      if (searchTerm) searchParams.query = searchTerm;
      if (selectedTribe && selectedTribe !== 'all') searchParams.tribe_name = selectedTribe;
      if (selectedRegion && selectedRegion !== 'all') searchParams.location = selectedRegion;
      if (selectedYear && selectedYear !== 'all') {
        const year = parseInt(selectedYear);
        searchParams.year_start = year;
        searchParams.year_end = year + 9; // Search within decade
      }

      const treatiesData = await treatyAPI.searchTreaties(searchParams);
      setTreaties(treatiesData);
    } catch (error) {
      console.error('Failed to search treaties:', error);
      toast({
        variant: "destructive",
        title: "Search Error",
        description: "Failed to search treaties. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Get unique regions and tribes for filter dropdowns
  const regions = [...new Set(treaties.map(t => t.location_signed || 'Unknown'))].filter(Boolean);
  const tribes = [...new Set(treaties.flatMap(t => t.parties_involved || []))]
    .filter(tribe => tribe !== 'United States' && tribe);

  const handleViewTreaty = (treaty) => {
    setSelectedTreaty(treaty);
    setShowTreatyModal(true);
  };

  const handleDownloadTreaty = (treaty) => {
    try {
      // Format treaty data for download
      const treatyData = `
TREATY SUMMARY
${treaty.name}
${treaty.official_title}

BASIC INFORMATION
Treaty Type: ${treaty.treaty_type?.replace('_', ' ') || 'Unknown'}
Signed: ${formatTreatyDate(treaty.signing_date)}
Location: ${treaty.location_signed || 'Unknown'}
Status: ${treaty.status || 'Unknown'}

PARTIES INVOLVED
${treaty.parties_involved ? treaty.parties_involved.join('\n') : 'Not specified'}

SUMMARY
${treaty.summary || treaty.historical_context || 'No summary available'}

KEY PROVISIONS
${treaty.key_provisions && treaty.key_provisions.length > 0 
  ? treaty.key_provisions.map((provision, index) => `${index + 1}. ${provision}`).join('\n')
  : 'No provisions listed'
}

MODERN IMPLICATIONS
${treaty.modern_implications && treaty.modern_implications.length > 0
  ? treaty.modern_implications.map((implication, index) => `• ${implication}`).join('\n')
  : 'No modern implications listed'
}

LAND AFFECTED
${treaty.land_affected || 'Not specified'}

---
Downloaded from TribalRoot.AI Treaty Library
Date: ${new Date().toLocaleDateString()}
      `.trim();

      // Create and trigger download
      const blob = new Blob([treatyData], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      // Create filename from treaty name
      const filename = treaty.name.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
      link.href = url;
      link.setAttribute('download', `${filename}_treaty_summary.txt`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Treaty Downloaded",
        description: `${treaty.name} summary has been downloaded successfully.`,
      });

      console.log(`Downloaded treaty summary: ${treaty.name}`);
    } catch (error) {
      console.error('Error downloading treaty:', error);
      toast({
        variant: "destructive",
        title: "Download Failed",
        description: "Failed to download treaty summary. Please try again.",
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          Treaty Library
          {!isLoading && treaties.length > 0 && (
            <span className="text-lg font-normal text-gray-600 ml-2">
              ({treaties.length} treaties found)
            </span>
          )}
        </h2>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Explore historical treaties, understand their terms, and discover how they impact modern tribal rights.
          Our comprehensive database contains {!isLoading && treaties.length > 0 ? treaties.length : 'hundreds of'} authentic treaty documents.
        </p>
      </div>

      {/* Filters */}
      <Card className="bg-white shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="w-5 h-5 mr-2" />
            Filter Treaties
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                <Input
                  placeholder="Search treaties..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Tribe Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Tribe</label>
              <Select value={selectedTribe} onValueChange={setSelectedTribe}>
                <SelectTrigger>
                  <SelectValue placeholder="All tribes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All tribes</SelectItem>
                  {tribes.map(tribe => (
                    <SelectItem key={tribe} value={tribe}>{tribe}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Region Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Region</label>
              <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                <SelectTrigger>
                  <SelectValue placeholder="All regions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All regions</SelectItem>
                  {regions.map(region => (
                    <SelectItem key={region} value={region}>{region}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Year Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Year</label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger>
                  <SelectValue placeholder="All years" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All years</SelectItem>
                  <SelectItem value="1780">1780s</SelectItem>
                  <SelectItem value="1790">1790s</SelectItem>
                  <SelectItem value="1800">1800s</SelectItem>
                  <SelectItem value="1810">1810s</SelectItem>
                  <SelectItem value="1820">1820s</SelectItem>
                  <SelectItem value="1830">1830s</SelectItem>
                  <SelectItem value="1840">1840s</SelectItem>
                  <SelectItem value="1850">1850s</SelectItem>
                  <SelectItem value="1860">1860s</SelectItem>
                  <SelectItem value="1870">1870s</SelectItem>
                  <SelectItem value="1880">1880s</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {isLoading && (
        <Card className="bg-white shadow-lg">
          <CardContent className="p-8 text-center">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Loading treaties...</p>
          </CardContent>
        </Card>
      )}

      {/* Treaty Results */}
      {!isLoading && (
        <div className="space-y-4">
          {treaties.length === 0 ? (
            <Card className="bg-white shadow-lg">
              <CardContent className="p-8 text-center">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No treaties found matching your criteria.</p>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedTribe('all');
                    setSelectedRegion('all');
                    setSelectedYear('all');
                  }}
                  className="mt-4"
                >
                  Clear Filters
                </Button>
              </CardContent>
            </Card>
          ) : (
            treaties.map((treaty) => (
              <Card key={treaty.id} className="bg-white shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader className="cursor-pointer" onClick={() => setExpandedTreaty(expandedTreaty === treaty.id ? null : treaty.id)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div>
                        <CardTitle className="text-xl">{treaty.name}</CardTitle>
                        <div className="flex items-center space-x-4 mt-2">
                          <Badge variant="outline" className="flex items-center">
                            <Calendar className="w-3 h-3 mr-1" />
                            {formatTreatyDate(treaty.signing_date, { year: 'numeric' })}
                          </Badge>
                          <Badge variant="outline" className="flex items-center">
                            <Map className="w-3 h-3 mr-1" />
                            {treaty.location_signed || 'Unknown'}
                          </Badge>
                          <Badge variant="outline" className="flex items-center">
                            <Users className="w-3 h-3 mr-1" />
                            {treaty.parties_involved ? 
                              treaty.parties_involved.filter(p => p !== 'United States').slice(0,2).join(', ') : 
                              'Multiple tribes'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    {expandedTreaty === treaty.id ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </div>
                </CardHeader>

                {expandedTreaty === treaty.id && (
                  <CardContent className="border-t">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-semibold mb-2">Summary</h4>
                          <div className="text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                            {treaty.summary || treaty.historical_context || 'No summary available'}
                          </div>
                        </div>

                        <div>
                          <h4 className="font-semibold mb-2">Parties Involved</h4>
                          <div className="space-y-1">
                            <p className="text-sm"><strong>Tribes:</strong> {
                              treaty.parties_involved ? 
                                treaty.parties_involved.filter(p => p !== 'United States').join(', ') : 
                                'Multiple tribes'
                            }</p>
                            <p className="text-sm"><strong>Other Party:</strong> United States</p>
                            <p className="text-sm"><strong>Date:</strong> {formatTreatyDate(treaty.signing_date)}</p>
                            <p className="text-sm"><strong>Location:</strong> {treaty.location_signed || 'Unknown'}</p>
                          </div>
                        </div>

                        {treaty.key_provisions && treaty.key_provisions.length > 0 && (
                          <div>
                            <h4 className="font-semibold mb-2">Key Provisions</h4>
                            <ul className="text-sm space-y-1">
                              {treaty.key_provisions.map((provision, index) => (
                                <li key={index} className="flex items-start">
                                  <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                                  <span className="break-words">{provision}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      <div className="space-y-4">
                        <div>
                          <h4 className="font-semibold mb-2">Treaty Information</h4>
                          <div className="space-y-2">
                            <div>
                              <strong className="text-sm">Type:</strong>
                              <Badge 
                                variant="outline"
                                className="ml-2 capitalize"
                              >
                                {treaty.treaty_type?.replace('_', ' ')}
                              </Badge>
                            </div>
                            <div>
                              <strong className="text-sm">Status:</strong>
                              <Badge 
                                variant={treaty.status === 'active' ? 'default' : 'secondary'}
                                className="ml-2 capitalize"
                              >
                                {treaty.status}
                              </Badge>
                            </div>
                          </div>
                        </div>

                        {treaty.modern_implications && treaty.modern_implications.length > 0 && (
                          <div>
                            <h4 className="font-semibold mb-2">Modern Implications</h4>
                            <ul className="text-sm space-y-1">
                              {treaty.modern_implications.map((implication, index) => (
                                <li key={index} className="flex items-start text-gray-700">
                                  <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                                  {implication}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {treaty.land_affected && (
                          <div>
                            <h4 className="font-semibold mb-2">Land Affected</h4>
                            <p className="text-sm text-gray-700">{treaty.land_affected}</p>
                          </div>
                        )}

                        <div className="flex space-x-2 pt-4">
                          <Button 
                            size="sm" 
                            className="flex items-center"
                            onClick={() => handleViewTreaty(treaty)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View Details
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex items-center"
                            onClick={() => handleDownloadTreaty(treaty)}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))
          )}
        </div>
      )}

      {/* Treaty Detail Modal */}
      {showTreatyModal && selectedTreaty && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl sm:text-2xl font-bold truncate">{selectedTreaty.name}</h2>
                  <p className="text-blue-100 mt-1 text-sm sm:text-base truncate">{selectedTreaty.official_title}</p>
                </div>
                <div className="flex items-center gap-2 sm:space-x-3 sm:ml-4 flex-shrink-0">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleDownloadTreaty(selectedTreaty)}
                    className="flex items-center bg-white text-blue-600 hover:bg-blue-50 border-white text-xs sm:text-sm"
                  >
                    <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    <span className="hidden xs:inline">Download </span>Summary
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => setShowTreatyModal(false)}
                    className="flex items-center bg-red-500 hover:bg-red-600 text-white border-0 text-xs sm:text-sm"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Basic Information */}
                <div className="lg:col-span-1 space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <Calendar className="w-4 h-4 mr-2 text-blue-600" />
                      Treaty Information
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div>
                        <strong>Signed:</strong> {formatTreatyDate(selectedTreaty.signing_date)}
                      </div>
                      <div>
                        <strong>Location:</strong> {selectedTreaty.location_signed || 'Unknown'}
                      </div>
                      <div>
                        <strong>Type:</strong> 
                        <Badge variant="outline" className="ml-2 capitalize">
                          {selectedTreaty.treaty_type?.replace('_', ' ')}
                        </Badge>
                      </div>
                      <div>
                        <strong>Status:</strong> 
                        <Badge 
                          variant={selectedTreaty.status === 'active' ? 'default' : 'secondary'}
                          className="ml-2 capitalize"
                        >
                          {selectedTreaty.status}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <Users className="w-4 h-4 mr-2 text-blue-600" />
                      Parties Involved
                    </h3>
                    <ul className="text-sm space-y-1">
                      {selectedTreaty.parties_involved?.map((party, index) => (
                        <li key={index} className="flex items-start">
                          <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                          {party}
                        </li>
                      )) || <li className="text-gray-500">No parties listed</li>}
                    </ul>
                  </div>

                  {selectedTreaty.land_affected && (
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                        <Map className="w-4 h-4 mr-2 text-green-600" />
                        Land Affected
                      </h3>
                      <p className="text-sm text-gray-700">{selectedTreaty.land_affected}</p>
                    </div>
                  )}
                </div>

                {/* Right Column - Details */}
                <div className="lg:col-span-2 space-y-6">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <FileText className="w-4 h-4 mr-2 text-gray-600" />
                      Treaty Summary
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                        {selectedTreaty.summary || selectedTreaty.historical_context || 'No summary available'}
                      </div>
                    </div>
                  </div>

                  {selectedTreaty.key_provisions && selectedTreaty.key_provisions.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                        <FileText className="w-4 h-4 mr-2 text-amber-600" />
                        Key Provisions
                      </h3>
                      <div className="bg-amber-50 p-4 rounded-lg">
                        <ul className="space-y-2">
                          {selectedTreaty.key_provisions.map((provision, index) => (
                            <li key={index} className="flex items-start text-sm">
                              <span className="bg-amber-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center mr-2 mt-0.5 flex-shrink-0">
                                {index + 1}
                              </span>
                              <span className="break-words leading-relaxed">{provision}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {selectedTreaty.modern_implications && selectedTreaty.modern_implications.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                        <ExternalLink className="w-4 h-4 mr-2 text-green-600" />
                        Modern Implications
                      </h3>
                      <div className="bg-green-50 p-4 rounded-lg">
                        <ul className="space-y-2">
                          {selectedTreaty.modern_implications.map((implication, index) => (
                            <li key={index} className="flex items-start text-sm">
                              <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                              <span className="break-words leading-relaxed">{implication}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default TreatyLibrary;